import { IntegrationSetup } from "./IntegrationSetup.js";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plug, Plus, ShieldCheck } from "lucide-react";
import {
  AsyncSection,
  Badge,
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  Skeleton,
  Select,
  StatusPill,
  TabPanel,
  Tabs,
  ConfirmDialog,
  SettingsMetaCard,
} from "@playbookai/ui";
import {
  AdminAboutCard,
  AdminRelatedCard,
  AdminSection,
} from "../AdminSection.js";
import {
  createIntegrationAdminApi,
  createIntegrationConnectionsApi,
  useApiClient,
  type IntegrationAccessPolicy,
  type AdminIntegrationConnection,
  type IntegrationValidationResult,
  type IntegrationTemplate,
  type TenantIntegration,
} from "@playbookai/api-clients";
import { resolveDirectoryNames } from "../../../api/people-directory-api.js";
import {
  ADMIN_INTEGRATIONS_NEW_ROUTE,
  ADMIN_INTEGRATIONS_ROUTE,
  adminIntegrationRoute,
  adminIntegrationSectionRoute,
} from "../../../routes.js";
import { ManifestSettingsForm } from "./ManifestSettingsForm.js";
import { IntegrationsInventory } from "./IntegrationsInventory.js";
import { IntegrationToolCatalog } from "./IntegrationToolCatalog.js";
import { AccessPolicyEditor } from "./AccessPolicyEditor.js";
import { AccessGrants } from "./AccessGrants.js";
import { usePolicyDiscovery } from "./usePolicyDiscovery.js";
import {
  LIFECYCLE_LABELS,
  LIFECYCLE_MEANINGS,
  buildInventoryRows,
  type IntegrationLifecycle,
} from "./integration-inventory.js";
import { validationStatusLabel } from "./connection-mode.js";
import { useDateTimeFormatter } from "../../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "../AdminTimeZoneLabel.js";

/**
 * PB-1591 — the administrator's integration console.
 *
 * Admin-only. Since PB-4624 this renders at `/admin/integrations`, and the gate
 * is applied ONCE at the `/admin` parent (`RequireTenantAdmin`, PB-48) which
 * every admin child inherits — this route carries no wrapper of its own.
 * playbook-be asserts the `tenant-admin` realm role on every route including
 * the reads, so the server stays the authoritative gate.
 *
 * Two surfaces, one component, split by the presence of an `:integrationId`:
 * the tenant's INVENTORY of configured instances, and the EDITOR for one of
 * them. Adding is neither — it is its own route (`AddIntegration`), so the
 * OAuth round trip it contains has a URL to come back to.
 *
 * NOT ONE LINE HERE KNOWS A PROVIDER EXISTS. The instance's manifest drives the
 * entire configuration surface via `manifest-render.ts`. There is no provider
 * name in this file, no icon map, and no conditional on `providerKey` — that is
 * the ticket's defining property and it has its own test.
 */
export function IntegrationsSettings(): ReactElement {
  const config = useApiClient();
  const api = useMemo(() => createIntegrationAdminApi(config), [config]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { integrationId, section } = useParams<{
    integrationId: string;
    section: string;
  }>();

  /* Both reads belong to the INVENTORY branch, and the editor below reads
     neither — it resolves its own instance and its own template by id. Asking
     for the whole tenant's instances and the whole platform catalog in order to
     render one integration is two requests nobody needs. */
  const inventory = integrationId === undefined;
  const templates = useQuery({
    queryKey: ["integration-templates"],
    queryFn: () => api.listTemplates(),
    enabled: inventory,
  });
  const integrations = useQuery({
    queryKey: ["tenant-integrations"],
    queryFn: () => api.listIntegrations(),
    enabled: inventory,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ["tenant-integrations"] });
  };

  if (integrationId !== undefined) {
    return (
      <IntegrationEditor
        key={integrationId}
        integrationId={integrationId}
        api={api}
        activeSection={section}
        onSectionChange={(next) =>
          navigate(
            next === "overview"
              ? adminIntegrationRoute(integrationId)
              : adminIntegrationSectionRoute(integrationId, next),
          )
        }
        onBack={() => navigate(ADMIN_INTEGRATIONS_ROUTE)}
        onSaved={invalidate}
      />
    );
  }

  // A failed read renders as a FAILURE, not as an empty inventory: "no
  // integrations yet" is a claim about the tenant, and the page has no
  // basis for it when the request never answered.
  /* Counts for the aside, from the same two reads the list is built from (d4):
     a count of what is on screen, never a claim about anything else. */
  /* Both error flags are checked, not only the data: TanStack keeps the last
     successful `data` through a failed refetch, and counts derived from it
     would sit beside the failure the body reports. */
  const inventoryRows =
    inventory &&
    !integrations.isError &&
    !templates.isError &&
    integrations.data &&
    templates.data
      ? buildInventoryRows(integrations.data, templates.data)
      : null;
  const countOf = (lifecycle: IntegrationLifecycle): number =>
    inventoryRows?.filter((row) => row.lifecycle === lifecycle).length ?? 0;

  const body =
    templates.isError || integrations.isError ? (
      <AsyncSection
        status="error"
        errorTitle="Couldn't load the integrations"
        errorDescription={
          ((templates.error ?? integrations.error) as Error | null)?.message
        }
        onRetry={() => {
          void templates.refetch();
          void integrations.refetch();
        }}
      >
        {null}
      </AsyncSection>
    ) : templates.isLoading || integrations.isLoading ? (
      <Skeleton className="h-64 w-full" />
    ) : (
      <div className="space-y-8">
        <IntegrationsInventory
          integrations={integrations.data ?? []}
          templates={templates.data ?? []}
        />
        <VsmPromotionPolicyCard
          api={api}
          integrations={integrations.data ?? []}
          templates={templates.data ?? []}
        />
      </div>
    );

  /* The header names no provider, for the same reason the page does not: which
     providers exist is a property of the deployment's registered connectors,
     not of the shell (PB-4624). */
  return (
    <AdminSection
      icon={Plug}
      title="Integrations"
      description="Connect this workspace to the systems your teams already work in, and control what each connection may reach."
      action={
        <Button
          variant="primary"
          className="shrink-0"
          onClick={() => navigate(ADMIN_INTEGRATIONS_NEW_ROUTE)}
        >
          <Plus className="size-4" aria-hidden="true" />
          Add integration
        </Button>
      }
      meta={
        <>
          {inventoryRows ? (
            <SettingsMetaCard
              icon={Plug}
              title="Inventory"
              rows={[
                { label: "Integrations", value: inventoryRows.length },
                { label: LIFECYCLE_LABELS.enabled, value: countOf("enabled") },
                {
                  label: LIFECYCLE_LABELS["not-enabled"],
                  value: countOf("not-enabled"),
                },
                ...(countOf("connector-unavailable") > 0
                  ? [
                      {
                        /* Its own words, not the pill's: the pill names a
                           row's state, this counts rows. */
                        label: "Unavailable connectors",
                        value: countOf("connector-unavailable"),
                      },
                    ]
                  : []),
              ]}
            />
          ) : null}
          {inventoryRows ? (
            /* What each status pill in the table means, said once. The rare
               state is listed only while the tenant holds one — the same rule
               the inventory's filter applies (a legend entry nothing on the
               page uses teaches nothing). */
            <AdminAboutCard title="What the statuses mean">
              {STATUS_LEGEND.filter(
                (lifecycle) =>
                  lifecycle !== "connector-unavailable" ||
                  countOf("connector-unavailable") > 0,
              ).map((lifecycle) => (
                <span key={lifecycle} className="mt-1.5 block first:mt-0">
                  <span className="font-medium text-pb-text">
                    {LIFECYCLE_LABELS[lifecycle]}
                  </span>
                  {": "}
                  {LIFECYCLE_MEANINGS[lifecycle]}
                </span>
              ))}
            </AdminAboutCard>
          ) : null}
          <AdminAboutCard title="About integrations">
            Workspace connections to outside systems. People connect their own
            accounts under each integration; admins see connection identity and
            health, never credentials.
          </AdminAboutCard>
          <AdminRelatedCard ids={["genai", "audit"]} />
        </>
      }
    >
      {body}
    </AdminSection>
  );
}

const STATUS_LEGEND = [
  "enabled",
  "not-enabled",
  "connector-unavailable",
] as const satisfies readonly IntegrationLifecycle[];

type Api = ReturnType<typeof createIntegrationAdminApi>;

function VsmPromotionPolicyCard({
  api,
  integrations,
  templates,
}: {
  readonly api: Api;
  readonly integrations: readonly TenantIntegration[];
  readonly templates: readonly IntegrationTemplate[];
}): ReactElement {
  const queryClient = useQueryClient();
  const policy = useQuery({
    queryKey: ["vsm-promotion-policy"],
    queryFn: () => api.getVsmPromotionPolicy(),
  });
  const [selectedId, setSelectedId] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);
  useEffect(() => {
    if (policy.data?.tenantIntegrationId) {
      setSelectedId(policy.data.tenantIntegrationId);
    }
  }, [policy.data?.tenantIntegrationId]);
  const templatesById = useMemo(
    () => new Map(templates.map((template) => [template.id, template])),
    [templates],
  );
  const options = integrations
    .filter((integration) => {
      if (integration.id === policy.data?.tenantIntegrationId) return true;
      const template = templatesById.get(integration.templateId);
      return (
        integration.enabled &&
        template?.protocol === "mcp" &&
        template.status === "available"
      );
    })
    .map((integration) => ({
      value: integration.id,
      label: `${integration.displayName}${integration.enabled ? "" : " — disabled"}`,
      disabled: !integration.enabled,
    }));
  const selected = integrations.find(
    (integration) => integration.id === policy.data?.tenantIntegrationId,
  );
  const setPolicy = useMutation({
    mutationFn: (tenantIntegrationId: string) =>
      api.setVsmPromotionPolicy(tenantIntegrationId),
    onSuccess: (next) => {
      queryClient.setQueryData(["vsm-promotion-policy"], next);
    },
  });
  const removePolicy = useMutation({
    mutationFn: () => api.removeVsmPromotionPolicy(),
    onSuccess: () => {
      queryClient.setQueryData(["vsm-promotion-policy"], null);
      setSelectedId("");
      setConfirmRemove(false);
    },
  });

  if (policy.isLoading) return <Skeleton className="h-52 w-full" />;
  return (
    <section className="space-y-4 rounded-xl border border-pb-border bg-pb-card p-5">
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-pb-text">
          VSM promotion requirement
        </h3>
        <p className="text-sm text-pb-text-muted">
          By default, a scored and unparked Kaizen can be promoted without an
          external Work Link. Enable this audited policy only when the workspace
          requires confirmed external-work evidence first.
        </p>
      </div>
      {policy.isError ? (
        <Alert tone="critical" title="Could not load the VSM policy">
          Reload this page before changing the requirement.
        </Alert>
      ) : null}
      {policy.data ? (
        <Alert
          tone={selected?.enabled === false ? "watch" : "info"}
          title="External-work evidence is required"
        >
          Confirmed existing links remain valid during an outage. Unlinked
          Kaizens are blocked when{" "}
          {selected?.displayName ?? "the selected integration"} is disabled or
          unavailable; admins can repair or re-enable it, or remove this policy.
        </Alert>
      ) : (
        <Alert tone="info" title="Default promotion behavior is active">
          External-work linking is optional and promotion remains independent.
        </Alert>
      )}
      <Select
        label="Required external-work MCP integration"
        hint="Only enabled MCP integrations are selectable. The integration must have an approved create-external-work tool."
        value={selectedId}
        placeholder="Choose an integration"
        options={options}
        onChange={setSelectedId}
        disabled={policy.isError || setPolicy.isPending}
      />
      {setPolicy.isError ? (
        <Alert tone="critical" title="Policy not changed">
          The selected integration must be enabled, MCP-based, and ready for
          governed external-work creation.
        </Alert>
      ) : null}
      {removePolicy.isError ? (
        <Alert tone="critical" title="Requirement not removed">
          The promotion requirement is still active. Reload the policy and try
          again.
        </Alert>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          disabled={
            !selectedId ||
            setPolicy.isPending ||
            selectedId === policy.data?.tenantIntegrationId
          }
          onClick={() => setPolicy.mutate(selectedId)}
        >
          {policy.data ? "Change requirement" : "Enable requirement"}
        </Button>
        {policy.data ? (
          <Button
            variant="secondary"
            disabled={removePolicy.isPending}
            onClick={() => setConfirmRemove(true)}
          >
            Remove requirement
          </Button>
        ) : null}
      </div>
      {/* ConfirmDialog (d2): promotion returns to its default, so the neutral
          tone. */}
      <ConfirmDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        title="Remove the external-work promotion requirement?"
        tone="neutral"
        confirmLabel="Remove requirement"
        loading={removePolicy.isPending}
        onConfirm={() => removePolicy.mutate()}
      >
        Kaizen promotion will return to the default behavior and no longer
        require a confirmed external Work Link.
      </ConfirmDialog>
    </section>
  );
}

/** Edit + validate one instance. */
function IntegrationEditor({
  integrationId,
  api,
  activeSection,
  onSectionChange,
  onBack,
  onSaved,
}: {
  integrationId: string;
  api: Api;
  activeSection: string | undefined;
  onSectionChange: (section: string) => void;
  onBack: () => void;
  onSaved: () => void | Promise<void>;
}): ReactElement {
  const instance = useQuery({
    queryKey: ["tenant-integration", integrationId],
    queryFn: () => api.getIntegration(integrationId),
  });
  const template = useQuery({
    queryKey: ["integration-template", instance.data?.templateId],
    queryFn: () => api.getTemplate(instance.data!.templateId),
    enabled: instance.data !== undefined,
  });

  const [draft, setDraft] = useState<{
    baseUpdatedAt: string;
    settings: Record<string, unknown>;
    policy: IntegrationAccessPolicy;
  } | null>(null);
  const [validation, setValidation] =
    useState<IntegrationValidationResult | null>(null);

  // PB-4734 (G9). Discovery lives HERE, not in the editor: the editor stays a
  // pure projection of the manifest, which is what the fictional-provider test
  // checks. Resolved once, so a page with six resource types issues one
  // connections read rather than six.
  const config = useApiClient();
  const connectionsApi = useMemo(
    () => createIntegrationConnectionsApi(config),
    [config],
  );
  const discovery = usePolicyDiscovery(integrationId);
  const loadResources = useCallback(
    async (connectionId: string, resourceType: string) =>
      (await connectionsApi.listResources(connectionId, resourceType)).map(
        (resource) => ({ id: resource.id, label: resource.displayName }),
      ),
    [connectionsApi],
  );
  const loadCapabilityInputs = useCallback(
    async (
      connectionId: string,
      capabilityKey: string,
      resourceId: string,
      field: string,
    ) => {
      // The capability's own form is the source of the allowed values — the
      // same call the member-facing action form makes. Nothing here knows what
      // the field means; it reads the options the connector returned for it.
      const spec = await connectionsApi.describeCapabilityForm(
        connectionId,
        capabilityKey,
        resourceId,
      );
      return (spec.fields.find((f) => f.key === field)?.options ?? []).map(
        (option) => ({ id: option.value, label: option.label }),
      );
    },
    [connectionsApi],
  );

  const save = useMutation({
    mutationFn: () =>
      api.updateIntegration(integrationId, {
        expectedUpdatedAt: draft?.baseUpdatedAt ?? instance.data!.updatedAt,
        settings: current().settings,
        accessPolicy: current().policy,
      }),
    onError: () => {
      void instance.refetch();
    },
    onSuccess: async (updated) => {
      setDraft(null);
      queryClient.setQueryData(["tenant-integration", integrationId], updated);
      await onSaved();
      await queryClient.invalidateQueries({
        queryKey: ["integration-readiness", integrationId],
      });
    },
  });
  /**
   * Lifecycle, and it lives HERE rather than on a row in the inventory.
   *
   * On a list it was a switch with no room beside it to say what flipping it
   * does, sitting one gap away from the access-policy switches it is nothing
   * like — and reachable by a mis-click while scanning. Turning a workspace
   * integration off stops work for everyone using it, which is a decision worth
   * opening the thing to make. The endpoint is unchanged and still writes its
   * own audit row.
   */
  const queryClient = useQueryClient();
  const validate = useMutation({
    mutationFn: () => api.validate(integrationId),
    onSuccess: setValidation,
  });

  function current(): {
    settings: Record<string, unknown>;
    policy: IntegrationAccessPolicy;
  } {
    return (
      draft ?? {
        settings: { ...(instance.data?.settings ?? {}) },
        policy: instance.data?.accessPolicy ?? {},
      }
    );
  }

  /**
   * Every draft write goes through here so the "Saved." confirmation cannot
   * outlive the save it describes. TanStack keeps the last mutation result
   * indefinitely, so editing after a save would otherwise leave "Saved."
   * standing over unsaved changes — worse than showing nothing at all, because
   * it is a positive claim rather than an absence.
   */
  function editDraft(next: {
    settings: Record<string, unknown>;
    policy: IntegrationAccessPolicy;
  }): void {
    save.reset();
    setDraft({
      ...next,
      baseUpdatedAt: draft?.baseUpdatedAt ?? instance.data!.updatedAt,
    });
  }

  if (instance.isError || template.isError) {
    return (
      <p role="alert" className="text-sm text-pb-red-text">
        Could not load this integration. Close and try again.
      </p>
    );
  }
  if (
    instance.isLoading ||
    instance.data === undefined ||
    template.data === undefined
  ) {
    return <Skeleton className="h-48 w-full" />;
  }

  const value = current();

  const tabs = [
    { value: "overview", label: "Overview" },
    { value: "connections", label: "Connections" },
    ...(template.data.protocol === "mcp"
      ? [{ value: "tool-access", label: "Tool access" }]
      : []),
    { value: "people", label: "People" },
  ];
  const activeTab = tabs.some((tab) => tab.value === activeSection)
    ? (activeSection ?? "overview")
    : "overview";
  const heading =
    tabs.find((tab) => tab.value === activeTab)?.label ?? "Overview";
  const subtitle: Record<string, string> = {
    overview:
      "Configure this integration and its workspace-wide access policy.",
    connections:
      "See how people connect their own accounts to this workspace integration.",
    "tool-access":
      "Decide which remote tools this workspace may use and how they are classified.",
    people: "Choose who may connect and use this integration.",
  };

  return (
    <AdminSection
      icon={Plug}
      title={heading}
      description={subtitle[activeTab] ?? subtitle.overview ?? ""}
      breadcrumb={
        <div className="flex items-center gap-2 text-sm">
          <Button variant="ghost" className="-ms-2" onClick={onBack}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            Integrations
          </Button>
          <span className="text-pb-text-muted" aria-hidden="true">
            /
          </span>
          <span className="font-medium text-pb-text">
            {instance.data.displayName}
          </span>
        </div>
      }
      meta={
        <>
          <AdminAboutCard title="About this integration">
            Workspace access, the people allowed to connect, and the remote
            tools it may reach are decided here. Turning workspace access off
            stops every person's work through it and refuses new authorizations,
            but destroys nothing.
          </AdminAboutCard>
          <AdminRelatedCard ids={["genai", "audit"]} />
        </>
      }
    >
      <Tabs
        /* Up to four tabs; wrap at phone width rather than widen the lane. */
        className="min-w-0 [&>[role=tablist]]:flex-wrap"
        aria-label={`${instance.data.displayName} integration sections`}
        items={tabs}
        value={activeTab}
        onValueChange={onSectionChange}
      >
        <TabPanel value="overview" padding="lg">
          <IntegrationSetup instance={instance.data}>
            <div className="space-y-5 rounded-xl border border-pb-border bg-pb-card p-5">
              <ManifestSettingsForm
                manifest={template.data.manifest}
                values={value.settings}
                onChange={(key, next) =>
                  editDraft({
                    ...value,
                    settings: { ...value.settings, [key]: next },
                  })
                }
              />

              <div className="space-y-3 border-t border-pb-border pt-5">
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold">Workspace policy</h3>
                  <p className="text-xs text-pb-text-muted">
                    This is the maximum the workspace permits. It does not turn
                    on the integration or make tools available to every person.
                  </p>
                </div>
                <AccessPolicyEditor
                  manifest={template.data.manifest}
                  policy={value.policy}
                  onChange={(policy) => editDraft({ ...value, policy })}
                  tenantIntegrationId={integrationId}
                  discovery={discovery}
                  loadResources={loadResources}
                  loadCapabilityInputs={loadCapabilityInputs}
                />
              </div>

              <MutationError error={save.error} />
              {save.isError &&
              draft &&
              draft.baseUpdatedAt !== instance.data.updatedAt ? (
                <Alert tone="watch" title="Saved configuration changed">
                  <p>
                    Your draft is preserved. Compare it with the latest saved
                    values before retrying.
                  </p>
                  <pre className="max-h-48 overflow-auto whitespace-pre-wrap text-xs">
                    {JSON.stringify(
                      {
                        settings: instance.data.settings,
                        policy: instance.data.accessPolicy,
                      },
                      null,
                      2,
                    )}
                  </pre>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setDraft({
                        ...draft,
                        baseUpdatedAt: instance.data!.updatedAt,
                      });
                      save.reset();
                    }}
                  >
                    Keep my draft against these saved values
                  </Button>
                </Alert>
              ) : null}
              {save.isSuccess ? (
                <p role="status" className="text-sm text-pb-text-muted">
                  Saved.
                </p>
              ) : null}

              <div className="flex gap-2">
                <Button
                  variant="primary"
                  onClick={() => save.mutate()}
                  disabled={save.isPending}
                >
                  Save changes
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => validate.mutate()}
                  disabled={validate.isPending}
                >
                  Validate
                </Button>
              </div>
              {validation !== null ? (
                <ValidationReport result={validation} />
              ) : null}
            </div>
          </IntegrationSetup>
        </TabPanel>

        <TabPanel value="connections" padding="lg">
          <IntegrationConnections integrationId={integrationId} api={api} />
        </TabPanel>

        {template.data.protocol === "mcp" ? (
          <TabPanel value="tool-access" padding="lg">
            <IntegrationSetup instance={instance.data}>
              <IntegrationToolCatalog integrationId={integrationId} />
            </IntegrationSetup>
          </TabPanel>
        ) : null}

        <TabPanel value="people" padding="lg">
          <div className="rounded-xl border border-pb-border bg-pb-card p-5">
            <AccessGrants integrationId={integrationId} api={api} />
          </div>
        </TabPanel>
      </Tabs>
    </AdminSection>
  );
}

function IntegrationConnections({
  integrationId,
  api,
}: {
  integrationId: string;
  api: Api;
}): ReactElement {
  const formatter = useDateTimeFormatter();
  const { dateTimeOr } = formatter;
  const connections = useQuery({
    queryKey: ["integration-admin-connections", integrationId],
    queryFn: () => api.listConnections(integrationId),
  });
  const subjectIds = useMemo(
    () => [
      ...new Set(
        (connections.data ?? []).flatMap((connection) =>
          connection.subjectId === null ? [] : [connection.subjectId],
        ),
      ),
    ],
    [connections.data],
  );
  const names = useQuery({
    queryKey: ["integration-connection-names", integrationId, subjectIds],
    queryFn: ({ signal }) => resolveDirectoryNames(subjectIds, signal),
    enabled: subjectIds.length > 0,
  });
  const columns = useMemo<
    ReadonlyArray<DataTableColumn<AdminIntegrationConnection>>
  >(
    () => [
      {
        id: "person",
        width: "26%",
        header: "Person",
        cardRole: "title",
        cell: (connection) =>
          connection.subjectId === null ? (
            <span className="font-medium">Workspace account</span>
          ) : (
            <span className="font-medium">
              {names.data?.[connection.subjectId] ?? connection.subjectId}
            </span>
          ),
      },
      {
        id: "account",
        width: "26%",
        header: "Connected account",
        cardRole: "subtitle",
        cell: (connection) => (
          <span className="block min-w-0">
            <span className="block truncate">
              {connection.externalPrincipalName ?? connection.displayName}
            </span>
            {connection.externalTenantId === null ? null : (
              <span className="block truncate text-xs text-pb-text-muted">
                {connection.externalTenantId}
              </span>
            )}
          </span>
        ),
      },
      {
        id: "transport",
        width: "16%",
        header: "Connection",
        cardRole: "meta",
        cell: (connection) => connection.transportMode.toUpperCase(),
      },
      {
        id: "status",
        width: "16%",
        header: "Status",
        cardRole: "badge",
        cell: (connection) => (
          <StatusPill tone={connectionStatusTone(connection.status)}>
            {connectionStatusLabel(connection.status)}
          </StatusPill>
        ),
      },
      {
        id: "verified",
        width: "16%",
        header: (
          <AdminTimeZoneLabel label="Last checked" formatter={formatter} />
        ),
        cardRole: "meta",
        cell: (connection) =>
          dateTimeOr(connection.lastVerifiedAt, "Not checked"),
      },
    ],
    [names.data, dateTimeOr, formatter],
  );

  return (
    <div className="space-y-3">
      <p className="text-sm text-pb-text-muted">
        People manage their own authorization. Admins can see connection
        identity and health, but never credentials.
      </p>
      <DataTable
        layout="fixed"
        responsive
        textSize="small"
        /* No `nowrap`: these cells carry prose and pills, so they wrap to
           their column instead of clipping at its edge. */
        pinFirstColumn
        caption="Connections for this integration"
        /* Was a bare red paragraph that replaced the table — a fifth way of
           saying "this failed", with no way to try again. */
        error={
          connections.isError
            ? {
                title: "Couldn't load the connections",
                description: (connections.error as Error | null)?.message,
                onRetry: () => {
                  void connections.refetch();
                },
              }
            : undefined
        }
        columns={columns}
        data={connections.data ?? []}
        getRowId={(connection) => connection.id}
        loading={connections.isLoading}
        emptyState={
          <EmptyState
            icon={<Plug className="size-5" />}
            title="No one has connected yet"
            description="People with access can connect their own account from Settings → Connections."
          />
        }
      />
    </div>
  );
}

function connectionStatusTone(
  status: string,
): "healthy" | "watch" | "critical" | "neutral" {
  if (status === "active") return "healthy";
  if (status === "pending" || status === "expired") return "watch";
  if (status === "failed") return "critical";
  return "neutral";
}

function connectionStatusLabel(status: string): string {
  return status
    .split("_")
    .map((word, index) =>
      index === 0 ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word,
    )
    .join(" ");
}

function ValidationReport({
  result,
}: {
  result: IntegrationValidationResult;
}): ReactElement {
  return (
    <div className="space-y-2">
      <p className="flex items-center gap-2 text-sm font-medium">
        <ShieldCheck className="w-4 h-4" aria-hidden="true" />
        {result.ok
          ? "Configuration looks good"
          : "Configuration needs attention"}
      </p>
      <ul className="space-y-1">
        {result.checks.map((check) => (
          <li key={check.id} className="flex items-start gap-2 text-sm">
            {/* The status WORD is the signal, not a colour. `Badge` offers
                solid/outline only, and colour alone would fail the same
                accessibility bar anyway — `fail` reads as failure to a
                screen reader and to someone who cannot distinguish red. */}
            <Badge variant={check.status === "pass" ? "outline" : "soft"}>
              {validationStatusLabel(check.status)}
            </Badge>
            <span>
              <span className="font-medium">{check.label}</span> —{" "}
              {check.message}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Server-side validation is the authority, so its message is shown verbatim
 * rather than re-derived here. The backend reports every policy violation at
 * once and never quotes a submitted value, so this is safe to render.
 */
function MutationError({ error }: { error: unknown }): ReactElement | null {
  if (error === null || error === undefined) return null;
  const message = error instanceof Error ? error.message : "Something failed.";
  return (
    <p role="alert" className="text-sm text-pb-red-text">
      {message}
    </p>
  );
}
